import * as React from "react";
import {
  Grid,
  Link,
  InputLabel,
  Button,
  TextField,
  MenuItem,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";

// settings
import { getHeaders } from "../utils/HeaderHelper";
import { AuthenticationContext } from "../Context";

// components
import FormPanel from "./FormPanel";
import CategoryManagement from "./CategoryManagement";
import ResourceSelection from "./ResourceSelection";

// helper
import { useRef, useState } from "react";
import { toTitleCase } from "../utils/CaseFormatHelper";
import { normalizeWhitespaceForSave } from "../utils/WhitespaceHelper";

// hooks
import { useCustomerPermission } from "../hooks/useCustomerPermission";
import UnsavedChangesModal from "../modals/UnsavedChangesModal";
import { useHistory } from "react-router-dom";
import { questionnaireTypeToDisplay } from "../utils/StringHelper";
import { PrivilegesContext } from "../PrivilegesContext";
import { checkPrivileges } from "../utils/PrivilegesHelper";
import { useFlash } from "../FlashContext";

interface Props {}
// constants
const QUESTIONNAIRE_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;


const NewQuestionnaire: React.FC<Props> = (props: any) => {
  // auth
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { currentUser, ..._ } = authenticationSetting;
  const userPrivileges = React.useContext(PrivilegesContext);
  const { showToast } = useFlash();
  const { permitted: tabletPermitted } = useCustomerPermission(
    "Allow questionnaires to display on local device"
  );

  const { permitted: useDiabetesPermitted, loading } = useCustomerPermission(
    "Use NDPER Diabetes Program"
  );

  const { permitted: useOneProgramPermitted } =
    useCustomerPermission("Use ONE Program");

  // templateId
  const { id } = useParams();

  // questionnaire states
  const [questionnaireId, setQuestionnaireId] = React.useState<string>(null);
  const [categoryOptions, setCategoryOptions] = React.useState<any>([]);
  const [name, setName] = React.useState<string>("");
  const [description, setDescription] = React.useState<string>("");
  const [selectedCategory, setSelectedCategory] = React.useState<any>({});
  const [diabetesCategory, setDiabetesCategory] = React.useState<any>({});
  const [oneProgramCategory, setOneProgramCategory] = React.useState<any>({});
  const [displayOnTablet, setDisplayOnTablet] = React.useState<boolean>(false);
  const [hideFromPatients, setHideFromPatients] =
    React.useState<boolean>(false);

  const [newField, setNewField] = React.useState<any>({
    title: "",
    type: "short_answer",
    static_note: "",
    options: [],
    _destroy: false,
    position: null,
  });

  const [questions, setQuestions] = React.useState<any>([]);
  const [published, setPublished] = React.useState<boolean>(false); //disable fields after published
  const [archived, setArchived] = React.useState<boolean>(false); //disable fields after archived
  const [renderingKey, setRenderingKey] = useState<number>(Math.random);
  const [showResourceSelection, setShowResourceSelection] =
    useState<boolean>(false);
  const [selectedResourceIds, setSelectedResourceIds] = useState<any>([]);
  const [selectedResources, setSelectedResources] = useState<any>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [questionnaireOptions, setQuestionnaireOptions] = useState<any>([]);
  const [questionnaireType, setQuestionnaireType] =
    React.useState<string>("standard");
  const [standardCategories, setStandardCategories] = React.useState<any>([]);

  const history = useHistory();

  // handling for space and trimming for name
  const handleNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value.slice(0, QUESTIONNAIRE_MAX_LENGTH);

    setHasUnsavedChanges(true);
    setName(value);
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value.slice(0, DESCRIPTION_MAX_LENGTH);

    setHasUnsavedChanges(true);
    setDescription(value);
  };


  // diabetes management updates
  React.useEffect(() => {
    if (loading) return;

    const options = [{ value: "standard", text: "Standard Questionnaire" }];

    if (
      useDiabetesPermitted ||
      currentUser.starfield_admin ||
      (currentUser?.starfield_central &&
        (!currentUser?.diabetes_questionnaire_exists ||
          questionnaireType == "diabetes_management"))
    ) {
      options.push({
        value: "diabetes_management",
        text: "NDPERS Diabetes Program",
      });
    }

    if (useOneProgramPermitted || currentUser.starfield_admin) {
      options.push({ value: "one_program", text: "ONE Program" });
    }

    setQuestionnaireOptions(options);

    switch (questionnaireType) {
      case "diabetes_management":
        setCategoryOptions([diabetesCategory]);
        setSelectedCategory(diabetesCategory);
        break;

      case "standard":
        setCategoryOptions(standardCategories);
        break;

      case "one_program":
        if (!oneProgramCategory) {
          showToast({
            title: "Configuration Error",
            text: "Please add ONE Program category",
            type: "error",
          });
          setQuestionnaireType("standard");
          break;
        }

        setCategoryOptions([oneProgramCategory]);
        setSelectedCategory(oneProgramCategory);
        break;

      default:
        break;
    }
  }, [currentUser, questionnaireType, standardCategories, loading]);

  // fetching inital data
  React.useEffect(() => {
    getAssets();
    if (id) {
      getQuestionnaire();
    }
  }, [renderingKey]);

  React.useEffect(() => {
    const nextval = questions?.length + 1;

    setNewField({
      title: "",
      type: "short_answer",
      static_note: "",
      options: [],
      _destroy: false,
      position:
        (questions[questions.length] && questions[questions.length].position
          ? questions[questions.length].position
          : questions.length) + 1,
    });
  }, [questions]);

  const getQuestionnaire = async () => {
    const response = await fetch(`/questionnaires/${id}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    });
    if (response.status === 404) {
      window.location.href = "/not-found";
      return;
    }
    const result = await response.json();
    if (result.success == false) {
      alert(result.error);
    } else {
      const questionnaire = result.resource;
      setQuestionnaireId(questionnaire?.id);
      setName(questionnaire?.name);
      setDescription(questionnaire?.description);
      setSelectedCategory(questionnaire?.questionnaire_category);
      setQuestions(questionnaire?.questions);
      setPublished(questionnaire?.status == "published");
      setArchived(questionnaire?.status == "archived");
      setSelectedResourceIds(questionnaire?.resource_item_ids);
      setDisplayOnTablet(questionnaire?.display_on_tablet);
      setHideFromPatients(questionnaire?.hide_from_patients);
      setQuestionnaireType(questionnaire?.questionnaire_type);
    }
  };

  const getAssets = () => {
    fetch(`/questionnaires_assets`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          alert(result.error);
        } else {
          // setCategoryOptions(result.resource.categories);
          setStandardCategories(result.resource.categories);
          setDiabetesCategory(result.resource.diabetes_management_cat);
          setOneProgramCategory(result.resource.one_program_cat);
        }
      })
      .catch((error) => {
        alert(error);
      });
  };

  // handlers
  const handleSave = (saveType) => {
    if (validateFields()) {
      let url = `/questionnaires`;
      let method = "POST";

      if (questionnaireId) {
        url = `/questionnaires/${questionnaireId}`;
        method = "PUT";
      }

      fetch(url, {
        method,
        headers: getHeaders(authenticationSetting.csrfToken),
        body: JSON.stringify({
          questionnaire: {
            name: normalizeWhitespaceForSave(name),
            description: normalizeWhitespaceForSave(description),
            category: selectedCategory.db_name,
            status: saveType,
            questions_attributes: questionsAttributes(),
            display_on_tablet: displayOnTablet,
            hide_from_patients: hideFromPatients,
            questionnaire_category_id: selectedCategory.id,
            questionnaire_type: questionnaireType,
          },
          resource_ids: selectedResourceIds, 
        }),
      })
        .then((result) => result.json())
        .then((result) => {
          if (result.success === false) {
            if (
              result.error ===
              "Validation failed: Questionnaire type Only one questionnaire of type 'ONE Program' is allowed."
            ) {
              showToast({
                title: "Validation Error",
                text: "Only one questionnaire of type 'ONE Program' is allowed.",
                type: "error",
              });
            } else if (
              result.error ===
              "Validation failed: Questionnaire type Only one questionnaire of type 'diabetes_management' is allowed."
            ) {
              showToast({
                title: "Validation Error",
                text: "Only one questionnaire of type 'NDPERS Diabetes Program' is allowed.",
                type: "error",
              });
            } else {
              showToast({
                title: "Error Saving Questionnaire",
                text: result.error,
                type: "error",
              });
            }
          } else {
            setQuestionnaireId(result.resource.id);
            setQuestions(result.resource?.questions);

            if (result.resource.status === "published") {
              setPublished(true);
            }

            sessionStorage.setItem(
              "pendingToast",
              JSON.stringify({
                title: "Questionnaire Saved",
                text: result.message,
                type: "success",
              })
            );
            setHasUnsavedChanges(false);
            window.location.href = `/new-questionnaire/${result.resource.id}`;
          }
        })
        .catch((error) => {
          console.log("I am in catch");
          alert(error);
        });
    }

    return false;
  };

  // questions to submit attributes
  const questionsAttributes = () => {
    return questions.map((question) => {
      return {
        id: question.id,
        title: normalizeWhitespaceForSave(question.title),
        position: question.position,
        _destroy: question._destroy,
        question_type: question.type,
        static_note: question.static_note,
        options_attributes: question.options,
      };
    });
  };

  // field validation
  const validateFields = () => {
    if (isBlank(name)) {
      showToast({
        title: "Invalid Name",
        text: "Name cannot be blank",
        type: "error",
      });
      return false;
    }

    if (isBlank(selectedCategory?.db_name)) {
      showToast({
        title: "Invalid Category",
        text: "Category cannot be blank",
        type: "error",
      });
      return false;
    }

    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];

      if (isBlank(question.title)) {
        showToast({
          title: "Invalid Question",
          text: "Question title cannot be blank",
          type: "error",
        });
        return false;
      }

      if (
        question.type === "true_false" ||
        question.type === "multiple_choice"
      ) {
        if (question.options.length === 0) {
          showToast({
            title: "Invalid Question",
            text:
              "Multiple choice or True/False need at least one option",
            type: "error",
          });
          return false;
        }
      }
    }

    return true;
  };

  const isBlank = (str) => {
    return !str || str === undefined || str.trim() === "";
  };

  // getting resources when selected
  React.useEffect(() => {
    fetch(`/filter_resource`, {
      method: "POST",
      headers: getHeaders(authenticationSetting.csrfToken),
      body: JSON.stringify({
        resource_ids: selectedResourceIds,
      }),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          alert(result.error);
        } else {
          setSelectedResources(result.resource);
        }
      })
      .catch((error) => {
        alert(error);
      });
  }, [selectedResourceIds]);

  const handleResourceRemove = (resourceId) => {
    let tempSelectedResources = [...selectedResourceIds];
    let removedList = tempSelectedResources.filter(
      (item) => item != resourceId
    );
    setSelectedResourceIds(removedList);
    setHasUnsavedChanges(true);
  };

  const toggleDisplayOnTablet = () => {
    setDisplayOnTablet(!displayOnTablet);
  };

  const toggleHideFromPatients = () => {
    setHideFromPatients(!hideFromPatients);
  };

  const handleCategoryChange = (categoryId) => {
    const selectedCate = categoryOptions?.find(
      (category) => category.id == parseInt(categoryId)
    );
    setSelectedCategory(selectedCate);
  };

  const latestActivityDetected = React.useRef(false);

  // not letting session out if there is mouse movement
  const handleActivityDetected = () => {
    latestActivityDetected.current = true;
  };

  const resetSession = () => {
    fetch(`/reset_timeout_session`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => {
        if (result.status == 200) {
          latestActivityDetected.current = false;
          setCounter(60);
          setSessionKey((prevKey) => prevKey + 1);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const [isLogoutWarningModalOpen, setIsLogoutWarningModalOpen] =
    useState(false);
  const [counter, setCounter] = useState(60);
  const SESSION_TIME = 7;
  const intervalIdRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const [sessionKey, setSessionKey] = useState(0);

  const resetTimers = () => {
    if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);

    intervalIdRef.current = setInterval(() => {
      if (latestActivityDetected.current) {
        resetSession();
      }
    }, 20000);

    warningTimeoutRef.current = setTimeout(() => {
      setIsLogoutWarningModalOpen(true);
    }, (SESSION_TIME - 1) * 60 * 1000);
  };

  React.useEffect(() => {
    resetTimers();

    return () => {
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    };
  }, [sessionKey]);

  const handleLogout = () => {
    fetch("/users/sign_out", {
      method: "DELETE",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((response) => {
        if (response.ok) {
          window.location.href = "/";
        } else {
          showToast({
            title: "Unable to sign out",
            text: "Failed to sign out. Please try again.",
            type: "error"
          });
        }
      })
      .catch((error) => {
        showToast({
          title: "Unable to sign out",
          text: "Something went wrong during sign out. Please try again.",
          type: "error"
        });
      });
  };

  React.useEffect(() => {
    let countdownInterval;
    if (isLogoutWarningModalOpen) {
      countdownInterval = setInterval(() => {
        setCounter((prevCounter) => {
          if (prevCounter === 1) {
            handleLogout();
          }
          return prevCounter - 1;
        });
      }, 1000);
    }

    return () => {
      clearInterval(countdownInterval);
    };
  }, [isLogoutWarningModalOpen]);

  const handleResumeSession = () => {
    resetSession();
    setIsLogoutWarningModalOpen(false);
    setCounter(60);
  };

  return (
    <div className="main-content-outer">
      <Dialog
        open={isLogoutWarningModalOpen}
        onClose={handleLogout}
        disableEscapeKeyDown={true}
      >
        <DialogTitle>Session Timeout Warning</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You will be logged out after {counter} seconds.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 2 }}>
          <Button onClick={handleResumeSession} sx={{ background: "#F88909" }}>
            Resume your session
          </Button>
          <Button onClick={handleLogout} color="secondary">
            Logout
          </Button>
        </DialogActions>
      </Dialog>
      {showResourceSelection ? (
        <ResourceSelection
          setSelectedResourceIds={(ids) => {
            setSelectedResourceIds(ids);
            setHasUnsavedChanges(true);
          }}
          selectedResourceIds={selectedResourceIds}
          setShowResourceSelection={setShowResourceSelection}
          setHasUnsavedChanges={setHasUnsavedChanges}
          onResourcesChange={handleActivityDetected}
        />
      ) : (
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          className="main-content"
          sx={{ paddingLeft: "138px" }}
          // onMouseMove={handleActivityDetected}
        >
          <UnsavedChangesModal unsavedChanges={hasUnsavedChanges} />
          <Grid
            item
            xs={12}
            id="provider-action-report"
            className="patient-edit-container patient-edit-form provider-action-report new-questionnaire-container"
          >
            <Grid container>
              <Grid
                className="patient-edit-header"
                container
                justifyContent="space-between"
              >
                <Grid item xs={4}>
                  <p className="secondary-label" style={{ marginLeft: "0px" }}>
                    Add New Questionnaire
                  </p>
                </Grid>

                <Grid item xs={6} className="q-btn-container">
                  <Link
                    style={{ marginRight: "16px" }}
                    onClick={() => history.push("/questionnaires-list")}
                  >
                    Back to List
                  </Link>
                  {!published && !archived && (
                    <Button
                      className="orange-btn"
                      onClick={() => handleSave("draft")}
                      style={{
                        height: "40px",
                        marginRight: "16px",
                        paddingLeft: "16px",
                        paddingRight: "16px",
                      }}
                      disabled={published || archived}
                    >
                      Save Draft
                    </Button>
                  )}
                </Grid>
              </Grid>
              <Grid container>
                {/* column 1 */}
                <Grid item xs={3}>
                  <Grid container className="form-container">
                    <Grid
                      item
                      xs={12}
                      className="patient-info-left-container form-left-container"
                      style={{
                        marginLeft: "30px",
                      }}
                    >
                      <Grid container>
                        <Grid item xs={12}>
                          <InputLabel
                            htmlFor="name"
                            className="field-label"
                            style={{
                              color: "#000000",
                              fontWeight: 700,
                              fontSize: "16px",
                            }}
                          >
                            Questionnaire Name*
                          </InputLabel>
                        </Grid>
                        <Grid item xs={12} className="field-container">
                          <TextField
                            id="name"
                            size="small"
                            value={name}
                            className="the-field"
                            required
                            variant="outlined"
                            disabled={published || archived}
                            placeholder="Enter Name"
                            inputProps={{ maxLength: QUESTIONNAIRE_MAX_LENGTH }}
                            onKeyUp={() => {
                              setHasUnsavedChanges(true);
                              handleActivityDetected();
                            }}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                            ) => {
                              let { value } = event.target;

                              // Enforce max length
                              if (value.length > QUESTIONNAIRE_MAX_LENGTH) {
                                value = value.slice(0, QUESTIONNAIRE_MAX_LENGTH);
                              }

                              setHasUnsavedChanges(true);
                              setName(value);
                            }}
                          />
                        </Grid>
                      </Grid>

                      <Grid container>
                        <Grid item xs={12}>
                          <InputLabel
                            htmlFor="description"
                            className="field-label"
                            style={{
                              color: "#000000",
                              fontWeight: 700,
                              fontSize: "16px",
                            }}
                          >
                            Description
                          </InputLabel>
                        </Grid>
                        <Grid item xs={12} className="field-container">
                          <TextField
                            id="description"
                            size="small"
                            value={description}
                            className="the-field"
                            required
                            variant="outlined"
                            disabled={published || archived}
                            placeholder="Enter Description"
                            inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
                            onKeyUp={() => {
                              setHasUnsavedChanges(true);
                              handleActivityDetected();
                            }}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                            ) => {
                              let { value } = event.target;
                              if (value.length > DESCRIPTION_MAX_LENGTH) {
                                value = value.slice(0, DESCRIPTION_MAX_LENGTH);
                              }
                              setHasUnsavedChanges(true);
                              setDescription(value);
                            }}
                          />
                        </Grid>
                      </Grid>

                      <Grid container>
                        <Grid item xs={12}>
                          <InputLabel
                            htmlFor="Category"
                            className="field-label"
                            style={{
                              color: "#000000",
                              fontWeight: 700,
                              fontSize: "16px",
                            }}
                          >
                            Category*
                          </InputLabel>
                        </Grid>
                        <Grid item xs={12} className="field-container">
                          <TextField
                            id="category"
                            size="small"
                            value={`${selectedCategory?.id}`}
                            className="the-field"
                            required
                            variant="outlined"
                            onChange={(event) => {
                              handleCategoryChange(event.target.value);
                              setHasUnsavedChanges(true);
                              handleActivityDetected();
                            }}
                            select
                            disabled={published || archived}
                          >
                            {published || archived ? (
                              <MenuItem value={`${selectedCategory?.id}`}>
                                {selectedCategory?.display_name}
                              </MenuItem>
                            ) : (
                              categoryOptions?.map((catOption) => (
                                <MenuItem
                                  key={catOption?.id}
                                  value={catOption?.id}
                                >
                                  {catOption?.display_name}
                                </MenuItem>
                              ))
                            )}
                          </TextField>
                        </Grid>
                      </Grid>
                      {currentUser?.starfield_central && (
                        <Grid container>
                          <Grid item xs={12}>
                            <InputLabel
                              htmlFor="questionnaire_type"
                              className="field-label"
                              style={{
                                color: "#000000",
                                fontWeight: 700,
                                fontSize: "16px",
                              }}
                            >
                              Starfield Questionnaire Type*
                            </InputLabel>
                          </Grid>
                          <Grid item xs={12} className="field-container">
                            <TextField
                              id="questionnaire_type"
                              size="small"
                              value={
                                published || archived
                                  ? questionnaireTypeToDisplay(
                                      questionnaireType
                                    )
                                  : questionnaireType.length > 0
                                  ? questionnaireType
                                  : "standard"
                              }
                              className="the-field"
                              required
                              variant="outlined"
                              onKeyUp={(event) => {
                                setHasUnsavedChanges(true);
                              }}
                              onChange={(event) => {
                                setQuestionnaireType(event.target.value);
                              }}
                              select={!published && !archived}
                              disabled={published || archived}
                              placeholder="Select Questionnaire Type"
                            >
                              {questionnaireOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.text}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        </Grid>
                      )}
                      <Grid container>
                        <Grid item xs={6}>
                          <InputLabel
                            htmlFor="Category"
                            className="field-label"
                            style={{
                              color: "#000000",
                              fontWeight: 700,
                              fontSize: "14px",
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            Display on Tablet?
                          </InputLabel>
                        </Grid>
                        <Grid item xs={6}>
                          <InputLabel
                            htmlFor="Category"
                            className="field-label"
                            style={{
                              color: "#000000",
                              fontWeight: 700,
                              fontSize: "14px",
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            Hide from Patients?
                          </InputLabel>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          className="field-container"
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <Switch
                            checked={displayOnTablet}
                            onChange={() => {
                              toggleDisplayOnTablet();
                              setHasUnsavedChanges(true);
                              handleActivityDetected();
                            }}
                            color="primary"
                            disabled={!tabletPermitted || published || archived}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          className="field-container"
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <Switch
                            checked={hideFromPatients}
                            onChange={() => {
                              toggleHideFromPatients();
                              setHasUnsavedChanges(true);
                              handleActivityDetected();
                            }}
                            color="primary"
                            disabled={!tabletPermitted || published || archived}
                          />
                        </Grid>
                      </Grid>
                      {!published && !archived && (
                        <>
                          <Grid container>
                            <CategoryManagement
                              setCategoryOptions={setCategoryOptions}
                              renderingKey={renderingKey}
                              setRenderingKey={setRenderingKey}
                              onCategoryChange={() => {
                                getAssets();
                                handleActivityDetected();
                              }}
                            />
                          </Grid>
                          <Grid container>*Required</Grid>
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
                {/* column 2 */}
                <Grid item xs={5} className="right-column">
                  <FormPanel
                    newField={newField}
                    questions={questions}
                    setQuestions={setQuestions}
                    readOnly={published || archived}
                    setHasUnsavedChanges={setHasUnsavedChanges}
                    onQuestionDataChange={() => {
                      handleActivityDetected();
                      setHasUnsavedChanges(true);
                    }}
                  />
                </Grid>

                {/* column 3 resource column */}
                <Grid
                  item
                  xs={4}
                  style={{ paddingRight: "10px", paddingLeft: "10px" }}
                >
                  <Grid
                    container
                    className="question-container"
                    style={{ marginTop: "30px" }}
                  >
                    <Grid
                      item
                      xs={12}
                      style={{
                        paddingTop: "10px",
                      }}
                    >
                      <InputLabel
                        htmlFor="Category"
                        className="field-label"
                        style={{
                          color: "black",
                          paddingLeft: "16px",
                          paddingBottom: "10px",
                        }}
                      >
                        Attached Resource
                      </InputLabel>
                    </Grid>
                    <Grid container item xs={12} className="field-container">
                      <Grid item xs={12}>
                        <TableContainer>
                          <Table size="small" aria-label="a dense table">
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  component="th"
                                  className="bold-font-face"
                                >
                                  Type
                                </TableCell>
                                <TableCell
                                  component="th"
                                  className="bold-font-face"
                                >
                                  Name of Resource
                                </TableCell>
                                <TableCell
                                  component="th"
                                  className="bold-font-face"
                                  align="center"
                                >
                                  View
                                </TableCell>
                                {!published && !archived && (
                                  <TableCell
                                    component="th"
                                    className="bold-font-face"
                                    align="center"
                                  >
                                    Remove
                                  </TableCell>
                                )}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedResources.map((resource, index) => (
                                <TableRow
                                  key={resource.id}
                                  className={
                                    index % 2 == 0 && "panel-grey-background"
                                  }
                                >
                                  <TableCell>
                                    {resource.resource_type == "pdf"
                                      ? "PDF"
                                      : toTitleCase(resource.resource_type)}
                                  </TableCell>
                                  <TableCell>{resource.name}</TableCell>
                                  <TableCell align="center">
                                    <Link
                                      onClick={handleActivityDetected}
                                      href={resource.link}
                                      target="_blank"
                                    >
                                      <VisibilityIcon />
                                    </Link>
                                  </TableCell>
                                  {!published && !archived && (
                                    <TableCell align="center">
                                      <Link
                                        onClick={() => {
                                          handleResourceRemove(resource.id);
                                          setHasUnsavedChanges(true);
                                          handleActivityDetected();
                                        }}
                                      >
                                        <DeleteIcon />
                                      </Link>
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                      {!published && !archived && (
                        <Link
                          onClick={() => {
                            setShowResourceSelection(true);
                          }}
                          className="grey-font"
                          style={{
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            fontSize: "15px",
                            paddingLeft: "10px",
                          }}
                        >
                          <img
                            src="https://starfield-static-assets.s3.us-east-2.amazonaws.com/menu-track.png"
                            width="20"
                            alt="Add Resource"
                            style={{ padding: "5px" }}
                          />
                          <b>Add Resource</b>
                        </Link>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default NewQuestionnaire;
